import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Layers, Bot, Settings2, Activity, Waypoints, Combine, Pencil, Table2, TrendingUp, Grid3x3,
  Target, BarChart3, Ruler, ClipboardList, Image, Crosshair, ListChecks, Box,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, MoreHorizontal, X,
} from 'lucide-react';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../ui/resizable';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '../ui/dropdown-menu';
import { DOCKS, FLOAT, PANELS, getPanel } from '../../lib/panel-registry';
import { MIN_FLOAT, pickActiveTab, touchRecent } from '../../lib/dock-layout';

/**
 * Workspace shell for the map: left dock (layers), right dock (properties / tools),
 * bottom dock (sections, charts, tasks...), the map in the centre, plus floating windows
 * for the panels that float (raster controls, find nearest, task console).
 *
 * State: `layout` + the bound actions from hooks/useDockLayout. Panel content is supplied
 * by the caller as `content[id]` (null / missing = nothing to show) and open / closed
 * stays with the existing hooks through `isOpen(id)` / `onClose(id)` (lib/panel-registry).
 * Panels are not rewritten: their own header and close button still work.
 * Below NARROW_PX every open panel moves into one bottom drawer.
 */

export const NARROW_PX = 900;
const DOCK_LABEL = { left: 'left', right: 'right', bottom: 'bottom' };

const ICONS = { Layers, Bot, Settings2, Activity, Waypoints, Combine, Pencil, Table2, TrendingUp, Grid3x3, Target, BarChart3, Ruler, ClipboardList, Image, Crosshair, ListChecks, Box };
function PanelIcon({ name, size = 13 }) {
  const Icon = ICONS[name] || Layers;
  return <Icon size={size} aria-hidden="true" />;
}

function useNarrow() {
  const get = () => (typeof window !== 'undefined' ? window.innerWidth < NARROW_PX : false);
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    const onResize = () => setNarrow(get());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return narrow;
}

const iconBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, flexShrink: 0,
  background: 'transparent', border: 'none', borderRadius: 4, cursor: 'pointer', color: 'var(--gs-text-3)',
};
const menuStyle = { background: 'var(--gs-bg-2)', color: 'var(--gs-text-1)', border: '1px solid var(--gs-border-hover)', fontSize: 12 };
const handleClass = 'bg-[var(--gs-border-hover)] hover:bg-[var(--gs-accent)] data-[resize-handle-state=drag]:bg-[var(--gs-accent)] data-[panel-group-direction=horizontal]:w-[3px] data-[panel-group-direction=vertical]:h-[3px]';

/** Tab menu: dock to left / right / bottom, pop out, reorder, close. */
function TabMenu({ id, title, dock, canMoveEarlier, canMoveLater, floatable, onMove, onFloat, onReorder, onClose, drawer }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label={`${title} panel menu`} title="Panel menu" style={iconBtn}><MoreHorizontal size={13} /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" style={menuStyle} data-testid={`panel-menu-${id}`}>
        {!drawer && <DropdownMenuLabel style={{ fontSize: 11, color: 'var(--gs-text-3)' }}>{title}</DropdownMenuLabel>}
        {!drawer && DOCKS.filter((d) => d !== dock).map((d) => (
          <DropdownMenuItem key={d} onSelect={() => onMove(id, d)}>Dock to {DOCK_LABEL[d]}</DropdownMenuItem>
        ))}
        {!drawer && floatable && <DropdownMenuItem onSelect={() => onFloat(id)}>Pop out to floating window</DropdownMenuItem>}
        {!drawer && (canMoveEarlier || canMoveLater) && <DropdownMenuSeparator />}
        {!drawer && canMoveEarlier && <DropdownMenuItem onSelect={() => onReorder(id, -1)}>Move tab earlier</DropdownMenuItem>}
        {!drawer && canMoveLater && <DropdownMenuItem onSelect={() => onReorder(id, 1)}>Move tab later</DropdownMenuItem>}
        {!drawer && <DropdownMenuSeparator />}
        <DropdownMenuItem onSelect={() => onClose(id)}>Close panel</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** One dock: a Radix Tabs strip plus the panel bodies (all mounted, inactive ones hidden to keep their state). */
function DockTabs({ dock, ids, active, recent = [], content, onActivate, onMove, onFloat, onReorder, onClose, onCollapse, collapseIcon, drawer = false }) {
  const current = pickActiveTab(ids, active, recent);
  return (
    <Tabs value={current} onValueChange={(id) => onActivate(dock, id)} data-testid={`dock-${dock}`}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: 'var(--gs-bg-2)', color: 'var(--gs-text-1)' }}>
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--gs-border)', background: 'var(--gs-bg-1)', flexShrink: 0 }}>
        <TabsList className="h-auto min-w-0 flex-1 justify-start gap-0 overflow-x-auto rounded-none p-0" style={{ background: 'transparent' }}>
          {ids.map((id, i) => {
            const p = getPanel(id);
            const isActive = id === current;
            return (
              <div key={id} style={{ display: 'flex', alignItems: 'center', flexShrink: 0, borderBottom: `2px solid ${isActive ? 'var(--gs-accent)' : 'transparent'}` }}>
                <TabsTrigger value={id} className="h-7 gap-1 rounded-none px-2 py-0 text-xs shadow-none"
                  style={{ background: 'transparent', color: isActive ? 'var(--gs-text-0)' : 'var(--gs-text-3)', boxShadow: 'none' }}>
                  <PanelIcon name={p.icon} /> {p.title}
                </TabsTrigger>
                <TabMenu id={id} title={p.title} dock={dock} drawer={drawer} floatable={p.floatable}
                  canMoveEarlier={i > 0} canMoveLater={i < ids.length - 1}
                  onMove={onMove} onFloat={onFloat} onReorder={(pid, dir) => onReorder(pid, i + dir)} onClose={onClose} />
              </div>
            );
          })}
        </TabsList>
        <button type="button" style={iconBtn} onClick={() => onCollapse(dock)} title={`Collapse ${drawer ? 'drawer' : `${dock} dock`}`} aria-label={`Collapse ${drawer ? 'drawer' : `${dock} dock`}`}>
          {collapseIcon}
        </button>
      </div>
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {ids.map((id) => (
          <TabsContent key={id} value={id} forceMount className="gs-dock-body absolute inset-0 mt-0 data-[state=inactive]:hidden" data-testid={`panel-${id}`}>
            {content[id]}
          </TabsContent>
        ))}
      </div>
    </Tabs>
  );
}

/** Collapsed dock: a thin strip of icons; clicking one expands the dock on that panel. */
function Rail({ dock, ids, onExpand }) {
  const vertical = dock !== 'bottom';
  return (
    <div data-testid={`rail-${dock}`} style={{
      display: 'flex', flexDirection: vertical ? 'column' : 'row', alignItems: 'center', gap: 2, padding: 2, flexShrink: 0,
      background: 'var(--gs-bg-1)', [vertical ? (dock === 'left' ? 'borderRight' : 'borderLeft') : 'borderTop']: '1px solid var(--gs-border)',
      ...(vertical ? { width: 28 } : { height: 28 }),
    }}>
      {ids.map((id) => {
        const p = getPanel(id);
        return (
          <button key={id} type="button" style={iconBtn} title={`${p.title} (expand ${dock} dock)`} aria-label={`Expand ${p.title}`} onClick={() => onExpand(dock, id)}>
            <PanelIcon name={p.icon} size={14} />
          </button>
        );
      })}
    </div>
  );
}

/** Absolute-positioned window inside the map area: drag by the header, resize from the corner, Esc closes. */
function FloatingWindow({ id, rect, boundsRef, onRect, onClose, onMove, children }) {
  const p = getPanel(id);
  const [live, setLive] = useState(null);
  const drag = useRef(null);
  const cur = live || rect;

  const bounds = () => {
    const b = boundsRef.current?.getBoundingClientRect();
    return { w: b?.width || 800, h: b?.height || 600 };
  };
  const begin = (e, mode) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { mode, sx: e.clientX, sy: e.clientY, start: cur };
  };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    const b = bounds();
    if (d.mode === 'move') {
      setLive({ ...d.start, x: Math.min(Math.max(0, d.start.x + dx), Math.max(0, b.w - 80)), y: Math.min(Math.max(0, d.start.y + dy), Math.max(0, b.h - 32)) });
    } else {
      setLive({ ...d.start, w: Math.max(MIN_FLOAT.w, Math.min(d.start.w + dx, b.w - d.start.x)), h: Math.max(MIN_FLOAT.h, Math.min(d.start.h + dy, b.h - d.start.y)) });
    }
  };
  const end = () => {
    if (!drag.current) return;
    drag.current = null;
    if (live) onRect(id, live);
    setLive(null);
  };

  return (
    <div role="dialog" aria-label={p.title} tabIndex={-1} data-testid={`float-${id}`}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(id); } }}
      style={{
        position: 'absolute', left: cur.x, top: cur.y, width: cur.w, height: cur.h, zIndex: 8, display: 'flex', flexDirection: 'column',
        background: 'var(--gs-bg-2)', color: 'var(--gs-text-1)', border: '1px solid var(--gs-border-hover)', borderRadius: 8,
        boxShadow: '0 6px 20px rgba(0,0,0,0.35)', overflow: 'hidden', outline: 'none',
      }}>
      <div onPointerDown={(e) => begin(e, 'move')} onPointerMove={move} onPointerUp={end} onPointerCancel={end}
        style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 4px 2px 8px', cursor: 'move', touchAction: 'none', userSelect: 'none', background: 'var(--gs-bg-1)', borderBottom: '1px solid var(--gs-border)', flexShrink: 0 }}>
        <PanelIcon name={p.icon} />
        <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--gs-text-0)' }}>{p.title}</span>
        <span onPointerDown={(e) => e.stopPropagation()}>
          <TabMenu id={id} title={p.title} dock={FLOAT} floatable={false} canMoveEarlier={false} canMoveLater={false}
            onMove={onMove} onFloat={() => {}} onReorder={() => {}} onClose={onClose} />
        </span>
        <button type="button" style={iconBtn} onPointerDown={(e) => e.stopPropagation()} onClick={() => onClose(id)} title="Close (Esc)" aria-label={`Close ${p.title}`}><X size={13} /></button>
      </div>
      <div className="gs-dock-body" style={{ flex: 1, minHeight: 0 }}>{children}</div>
      <div onPointerDown={(e) => begin(e, 'resize')} onPointerMove={move} onPointerUp={end} onPointerCancel={end} aria-hidden="true" title="Resize"
        style={{ position: 'absolute', right: 0, bottom: 0, width: 14, height: 14, cursor: 'nwse-resize', touchAction: 'none', borderRight: '3px solid var(--gs-text-4)', borderBottom: '3px solid var(--gs-text-4)', borderBottomRightRadius: 6 }} />
    </div>
  );
}

export default function DockLayout({ layout, actions, isOpen, onClose, content, children, registry = PANELS }) {
  const narrow = useNarrow();
  const centerRef = useRef(null);
  const panelRefs = { left: useRef(null), right: useRef(null), bottom: useRef(null) };
  const [dismissed, setDismissed] = useState(() => new Set());
  const [drawerActive, setDrawerActive] = useState(null);

  const visible = useCallback((id) => isOpen(id) && !dismissed.has(id) && !!content[id], [isOpen, dismissed, content]);
  const visibleIds = registry.map((p) => p.id).filter(visible);
  const visibleKey = visibleIds.join('|');

  // A dismissed panel that has closed in the hooks may be shown again next time it opens.
  const openKey = registry.map((p) => p.id).filter((id) => isOpen(id)).join('|');
  useEffect(() => {
    const open = new Set(openKey ? openKey.split('|') : []);
    setDismissed((prev) => {
      const next = new Set([...prev].filter((id) => open.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [openKey]);

  // Newly opened panel: bring its tab forward and expand its dock.
  const prevVisible = useRef(null);
  useEffect(() => {
    const now = visibleKey ? visibleKey.split('|') : [];
    if (prevVisible.current) now.filter((id) => !prevVisible.current.includes(id)).forEach((id) => actions.reveal(id));
    prevVisible.current = now;
  }, [visibleKey, actions]);

  // Most recently active tabs (newest first): when the active tab closes, the dock falls back
  // to the previously used open tab instead of the first tab in the dock.
  const [recent, setRecent] = useState([]);
  const activeKey = DOCKS.map((d) => layout.active[d] || '').join('|');
  useEffect(() => {
    setRecent((prev) => activeKey.split('|').reduce((r, id) => touchRecent(r, id), prev));
  }, [activeKey]);

  // Let the map re-measure after the layout settles.
  const sizeKey = `${layout.sizes.left}|${layout.sizes.right}|${layout.sizes.bottom}|${DOCKS.map((d) => layout.collapsed[d]).join()}|${narrow}`;
  useEffect(() => {
    const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(() => window.dispatchEvent(new Event('resize'))) : null;
    return () => { if (raf !== null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf); };
  }, [sizeKey]);

  const closePanel = useCallback((id) => {
    setDismissed((prev) => new Set(prev).add(id));
    onClose(id);
  }, [onClose]);

  const reorder = (id, index) => actions.reorder(id, index);
  const dockIds = (d) => layout.tabs[d].filter(visible);
  const floatIds = Object.keys(layout.floating).filter(visible);
  const commitSize = (d) => { const s = panelRefs[d].current?.getSize?.(); if (typeof s === 'number') actions.setSize(d, s); };

  const tabProps = { content, recent, onActivate: actions.setActive, onMove: actions.moveTo, onFloat: (id) => actions.float(id), onReorder: reorder, onClose: closePanel };

  const centre = (
    <div ref={centerRef} style={{ position: 'relative', height: '100%', minWidth: 0, minHeight: 0 }}>
      {children}
      {!narrow && floatIds.map((id) => (
        <FloatingWindow key={id} id={id} rect={layout.floating[id]} boundsRef={centerRef}
          onRect={actions.setFloatRect} onClose={closePanel} onMove={actions.moveTo}>
          {content[id]}
        </FloatingWindow>
      ))}
    </div>
  );

  // ── Narrow screens: one bottom drawer with every open panel ──
  if (narrow) {
    const ids = registry.map((p) => p.id).filter(visible);
    const drawerCollapsed = layout.collapsed.bottom;
    return (
      <div data-testid="dock-layout" data-mode="drawer" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <div style={{ flex: 1, minHeight: 0 }}>{centre}</div>
        {ids.length > 0 && (
          <div style={{ height: drawerCollapsed ? 32 : '45%', flexShrink: 0, borderTop: '1px solid var(--gs-border-hover)' }}>
            {drawerCollapsed ? (
              <button type="button" onClick={() => actions.setCollapsed('bottom', false)} aria-label="Expand drawer"
                style={{ ...iconBtn, width: '100%', height: 32, gap: 6, background: 'var(--gs-bg-1)', fontSize: 12 }}>
                <ChevronUp size={14} /> Panels ({ids.length})
              </button>
            ) : (
              <DockTabs dock="bottom" drawer ids={ids} active={drawerActive} {...tabProps}
                onActivate={(d, id) => setDrawerActive(id)}
                onCollapse={() => actions.setCollapsed('bottom', true)} collapseIcon={<ChevronDown size={14} />} />
            )}
          </div>
        )}
      </div>
    );
  }

  const left = dockIds('left');
  const right = dockIds('right');
  const bottom = dockIds('bottom');
  const showPanel = (d, ids) => ids.length > 0 && !layout.collapsed[d];
  const showRail = (d, ids) => ids.length > 0 && layout.collapsed[d];
  const expand = (d, id) => { actions.setActive(d, id); actions.setCollapsed(d, false); };
  const collapseIcons = { left: <ChevronLeft size={14} />, right: <ChevronRight size={14} />, bottom: <ChevronDown size={14} /> };

  const dockPanel = (d, ids, order) => (
    <>
      {d === 'right' && <ResizableHandle className={handleClass} onDragging={(dragging) => { if (!dragging) commitSize('right'); }} />}
      {d === 'bottom' && <ResizableHandle className={handleClass} onDragging={(dragging) => { if (!dragging) commitSize('bottom'); }} />}
      <ResizablePanel id={d} order={order} ref={panelRefs[d]} defaultSize={layout.sizes[d]}
        minSize={10} maxSize={d === 'bottom' ? 70 : 45}>
        <DockTabs dock={d} ids={ids} active={layout.active[d]} {...tabProps}
          onCollapse={actions.toggleCollapsed} collapseIcon={collapseIcons[d]} />
      </ResizablePanel>
      {d === 'left' && <ResizableHandle className={handleClass} onDragging={(dragging) => { if (!dragging) commitSize('left'); }} />}
    </>
  );

  return (
    <div data-testid="dock-layout" data-mode="docks" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ flex: 1, minHeight: 0 }}>
        <ResizablePanelGroup direction="vertical">
          <ResizablePanel id="workspace" order={1} minSize={25}>
            <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
              {showRail('left', left) && <Rail dock="left" ids={left} onExpand={expand} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <ResizablePanelGroup direction="horizontal">
                  {showPanel('left', left) && dockPanel('left', left, 1)}
                  <ResizablePanel id="map" order={2} minSize={25}>{centre}</ResizablePanel>
                  {showPanel('right', right) && dockPanel('right', right, 3)}
                </ResizablePanelGroup>
              </div>
              {showRail('right', right) && <Rail dock="right" ids={right} onExpand={expand} />}
            </div>
          </ResizablePanel>
          {showPanel('bottom', bottom) && dockPanel('bottom', bottom, 2)}
        </ResizablePanelGroup>
      </div>
      {showRail('bottom', bottom) && <Rail dock="bottom" ids={bottom} onExpand={expand} />}
    </div>
  );
}
